import React, { useContext } from "react"
import { Button, Col, Form, Row } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import { Formik } from "formik"
import _ from "lodash"

import {
  getFieldValues,
  getSectionFieldNames,
  getSettings,
  isOperatorTourIdea,
} from "@/onboarding/features/product/selectors"
import { updateFields } from "@/onboarding/features/product/Slice"
import FormikBootstrap from "@/components/formik-bootstrap"
import { ReferencedValueTypes, ReservedFields } from "@/onboarding/features/product/models/Product"
import { DAY_OF_WEEK_OPTIONS } from "@/onboarding/features/product/models/Field"
import ReferencedValueSelect from "@/onboarding/features/product/components/shared/ReferencedValueSelect"
import SubmitButton from "@/onboarding/features/product/components/shared/buttons/SubmitButton"
import SubmitAndApplyButton from "@/onboarding/features/product/components/shared/buttons/SubmitAndApplyButton"
import SubmittingSpinner from "@/onboarding/features/product/components/shared/SubmittingSpinner"
import { SubmittingContext } from "@/onboarding/features/product/helpers"
import TrackFormDirty from "@/onboarding/features/product/forms/TrackFormDirty"

const Edit = ({ setEditing }): JSX.Element => {
  const dispatch = useDispatch()
  const fieldNames = useSelector(getSectionFieldNames.tourDetails)
  const initialValues = useSelector(getFieldValues(...fieldNames))
  const fieldsSettings = useSelector(getSettings(...fieldNames))
  const [submitting, setSubmitting] = useContext(SubmittingContext)
  const isTourOperatorIdea = useSelector(isOperatorTourIdea)
  const cruiseLineFieldMessage = isTourOperatorIdea
    ? "Cruise line-specific value. Edits possible during contracting."
    : ""

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={(values) => {
        setSubmitting(true)
        dispatch(updateFields({ values }))
      }}
    >
      {({ handleSubmit }) => (
        <Form className="d-flex flex-column gap-3" onSubmit={handleSubmit}>
          <TrackFormDirty formId="tourDetails" />
          <Row>
            <Col md={3}>
              <FormikBootstrap.NumberInput
                name={ReservedFields.TARGET_MINIMUM_DEPARTURE_PAX}
                label="Minimum Per Departure"
                readOnly={!fieldsSettings[ReservedFields.TARGET_MINIMUM_DEPARTURE_PAX].writeable}
                showLockedIcon={
                  !fieldsSettings[ReservedFields.TARGET_MINIMUM_DEPARTURE_PAX].writeable
                }
                showRequired={fieldsSettings[ReservedFields.TARGET_MINIMUM_DEPARTURE_PAX].required}
              />
            </Col>
            <Col md={3}>
              <FormikBootstrap.NumberInput
                name={ReservedFields.TARGET_MAXIMUM_DEPARTURE_PAX}
                label="Maximum Per Departure"
                readOnly={!fieldsSettings[ReservedFields.TARGET_MAXIMUM_DEPARTURE_PAX].writeable}
                showLockedIcon={
                  !fieldsSettings[ReservedFields.TARGET_MAXIMUM_DEPARTURE_PAX].writeable
                }
                showRequired={fieldsSettings[ReservedFields.TARGET_MAXIMUM_DEPARTURE_PAX].required}
              />
            </Col>
            <Col md={3}>
              <FormikBootstrap.NumberInput
                name={ReservedFields.TARGET_MAXIMUM_CALL_PAX}
                label="Maximum Per Call"
                readOnly={!fieldsSettings[ReservedFields.TARGET_MAXIMUM_CALL_PAX].writeable}
                showLockedIcon={!fieldsSettings[ReservedFields.TARGET_MAXIMUM_CALL_PAX].writeable}
                showRequired={fieldsSettings[ReservedFields.TARGET_MAXIMUM_CALL_PAX].required}
              />
            </Col>
            <Col hidden>
              <FormikBootstrap.NumberInput
                name={ReservedFields.TARGET_DEPARTURES}
                label="Departures"
                readOnly={!fieldsSettings[ReservedFields.TARGET_DEPARTURES].writeable}
                showLockedIcon={!fieldsSettings[ReservedFields.TARGET_DEPARTURES].writeable}
                showRequired={fieldsSettings[ReservedFields.TARGET_DEPARTURES].required}
              />
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <ReferencedValueSelect
                name={ReservedFields.TOUR_TYPE}
                type={ReferencedValueTypes.TOUR_TYPE}
                label="Tour Types"
                isDisabled={!fieldsSettings[ReservedFields.TOUR_TYPE].writeable}
                showRequired={fieldsSettings[ReservedFields.TOUR_TYPE].required}
                emptyMessage={cruiseLineFieldMessage}
              />
            </Col>
            <Col md={3}>
              <ReferencedValueSelect
                name={ReservedFields.ACTIVITY_TYPE}
                type={ReferencedValueTypes.ACTIVITY_TYPE}
                label="Activity Type"
                isDisabled={!fieldsSettings[ReservedFields.ACTIVITY_TYPE].writeable}
                showRequired={fieldsSettings[ReservedFields.ACTIVITY_TYPE].required}
                emptyMessage={cruiseLineFieldMessage}
              />
            </Col>
            <Col md={3}>
              <ReferencedValueSelect
                name={ReservedFields.ACTIVITY_LEVEL}
                type={ReferencedValueTypes.ACTIVITY_LEVEL}
                label="Activity Level"
                isDisabled={!fieldsSettings[ReservedFields.ACTIVITY_LEVEL].writeable}
                showRequired={fieldsSettings[ReservedFields.ACTIVITY_LEVEL].required}
                emptyMessage={cruiseLineFieldMessage}
              />
            </Col>
            <Col md={3} className="d-flex align-items-center">
              {/* TODO(product): ADA_COMPLIANT must not be a required field. Any Switch fields must be optional by default, as it has a value by default. i.e if not ture then false */}
              <FormikBootstrap.Switch
                name={ReservedFields.ADA_COMPLIANT}
                label="ADA Compliant"
                disabled={!fieldsSettings[ReservedFields.ADA_COMPLIANT].writeable}
                showLockedIcon={!fieldsSettings[ReservedFields.ADA_COMPLIANT].writeable}
              />
            </Col>
          </Row>
          <FormikBootstrap.TextAreaInput
            name={ReservedFields.SHORT_TOUR_DESCRIPTION}
            label="Short Tour Description"
            readOnly={!fieldsSettings[ReservedFields.SHORT_TOUR_DESCRIPTION].writeable}
            showLockedIcon={!fieldsSettings[ReservedFields.SHORT_TOUR_DESCRIPTION].writeable}
            showRequired={fieldsSettings[ReservedFields.SHORT_TOUR_DESCRIPTION].required}
            helpText="Less Than 1000 Characters"
          />
          <FormikBootstrap.TextAreaInput
            rows={5}
            name={ReservedFields.TOUR_DESCRIPTION}
            label="Full Tour Description"
            readOnly={!fieldsSettings[ReservedFields.TOUR_DESCRIPTION].writeable}
            showLockedIcon={!fieldsSettings[ReservedFields.TOUR_DESCRIPTION].writeable}
            showRequired={fieldsSettings[ReservedFields.TOUR_DESCRIPTION].required}
          />
          <FormikBootstrap.TextAreaInput
            name={ReservedFields.TOUR_NOTES}
            label="Tour Notes"
            readOnly={!fieldsSettings[ReservedFields.TOUR_NOTES].writeable}
            showLockedIcon={!fieldsSettings[ReservedFields.TOUR_NOTES].writeable}
            showRequired={fieldsSettings[ReservedFields.TOUR_NOTES].required}
          />
          <FormikBootstrap.TextInput
            name={ReservedFields.TOP_THREE_REASONS}
            label="Top Three Reasons A Guest Should Buy This Tour"
            readOnly={!fieldsSettings[ReservedFields.TOP_THREE_REASONS].writeable}
            showLockedIcon={!fieldsSettings[ReservedFields.TOP_THREE_REASONS].writeable}
            showRequired={fieldsSettings[ReservedFields.TOP_THREE_REASONS].required}
          />
          <FormikBootstrap.TextInput
            name={ReservedFields.CANCELLATION_POLICY_DESCRIPTION}
            label="Cancellation Policy"
            readOnly={!fieldsSettings[ReservedFields.CANCELLATION_POLICY_DESCRIPTION].writeable}
            showLockedIcon={
              !fieldsSettings[ReservedFields.CANCELLATION_POLICY_DESCRIPTION].writeable
            }
            showRequired={fieldsSettings[ReservedFields.CANCELLATION_POLICY_DESCRIPTION].required}
          />
          <Row>
            <Col>
              <FormikBootstrap.MultiSelect
                name={ReservedFields.UNAVAILABLE_DAYS}
                label="Days On Which The Tour Will Be Unavailable"
                options={DAY_OF_WEEK_OPTIONS}
                showLockedIcon={!fieldsSettings[ReservedFields.UNAVAILABLE_DAYS].writeable}
                disabled={!fieldsSettings[ReservedFields.UNAVAILABLE_DAYS].writeable}
              />
            </Col>
            <Col>
              <FormikBootstrap.TextInput
                name={ReservedFields.OTHER_RELEVANT_UNAVAILABLE_INFORMATION}
                label="Other Relevant Information On Days And Times The Tour Is Not Available"
                readOnly={
                  !fieldsSettings[ReservedFields.OTHER_RELEVANT_UNAVAILABLE_INFORMATION].writeable
                }
                showLockedIcon={
                  !fieldsSettings[ReservedFields.OTHER_RELEVANT_UNAVAILABLE_INFORMATION].writeable
                }
              />
            </Col>
          </Row>
          {_.includes(fieldNames, ReservedFields.GROUND_OPERATOR) &&
            _.includes(fieldNames, ReservedFields.EVENING_EXCURSION) && (
              <Row>
                {_.includes(fieldNames, ReservedFields.GROUND_OPERATOR) && (
                  <Col>
                    <FormikBootstrap.TextInput
                      name={ReservedFields.GROUND_OPERATOR}
                      label="Ground Operator"
                      readOnly={!fieldsSettings[ReservedFields.GROUND_OPERATOR].writeable}
                      showLockedIcon={!fieldsSettings[ReservedFields.GROUND_OPERATOR].writeable}
                    />
                  </Col>
                )}
                {_.includes(fieldNames, ReservedFields.EVENING_EXCURSION) && (
                  <Col>
                    <FormikBootstrap.Switch
                      name={ReservedFields.EVENING_EXCURSION}
                      label="Evening Excursion"
                      disabled={!fieldsSettings[ReservedFields.EVENING_EXCURSION].writeable}
                      showLockedIcon={!fieldsSettings[ReservedFields.EVENING_EXCURSION].writeable}
                    />
                  </Col>
                )}
              </Row>
            )}
          <div className="d-flex gap-2">
            <SubmitButton />
            <SubmitAndApplyButton />
            <Button
              size="sm"
              variant="outline-danger"
              onClick={() => setEditing(false)}
              disabled={submitting}
            >
              <i className="fas fa-times me-2" />
              Cancel
            </Button>
            <SubmittingSpinner />
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default Edit
